import { normalizeId } from "../utils";

export const scriptKeys = {
  all: ["script"] as const,
  scenes: (projectId: string | number, documentId: string | number) =>
    ["scenes", normalizeId(projectId), normalizeId(documentId)] as const,
  scene: (projectId: string | number, documentId: string | number, sceneId: string | number) =>
    ["scene", normalizeId(projectId), normalizeId(documentId), normalizeId(sceneId)] as const,
  draftScenes: (projectId: string | number, documentId: string | number) =>
    ["script", "draft-scenes", normalizeId(projectId), normalizeId(documentId)] as const,
  draftScene: (projectId: string | number, documentId: string | number, sceneId: string | number) =>
    [
      "script",
      "draft-scene",
      normalizeId(projectId),
      normalizeId(documentId),
      normalizeId(sceneId),
    ] as const,
  /** Prefix for all per-scene draft queries in a document (use with `invalidateQueries`). */
  draftSceneDocumentPrefix: (projectId: string | number, documentId: string | number) =>
    ["script", "draft-scene", normalizeId(projectId), normalizeId(documentId)] as const,
  /** Under the per-scene prefix, so whole-document refreshes (restore, AI runs) refresh it too. */
  draftCharacterNames: (projectId: string | number, documentId: string | number) =>
    [
      "script",
      "draft-scene",
      normalizeId(projectId),
      normalizeId(documentId),
      "character-names",
    ] as const,
  draftSnapshots: (projectId: string | number, documentId: string | number) =>
    ["script", "draft-snapshots", normalizeId(projectId), normalizeId(documentId)] as const,
  draftSnapshot: (
    projectId: string | number,
    documentId: string | number,
    snapshotId: string | number
  ) =>
    [
      "script",
      "draft-snapshot",
      normalizeId(projectId),
      normalizeId(documentId),
      normalizeId(snapshotId),
    ] as const,
  draftSnapshotDiff: (
    projectId: string | number,
    documentId: string | number,
    snapshotId: string | number
  ) =>
    [
      "script",
      "draft-snapshot-diff",
      normalizeId(projectId),
      normalizeId(documentId),
      normalizeId(snapshotId),
    ] as const,
  draftSnapshotSceneDiff: (
    projectId: string | number,
    documentId: string | number,
    snapshotId: string | number,
    sceneId: string | number
  ) =>
    [
      "script",
      "draft-snapshot-scene-diff",
      normalizeId(projectId),
      normalizeId(documentId),
      normalizeId(snapshotId),
      normalizeId(sceneId),
    ] as const,
  /** Prefix for all per-snapshot scene-diff queries for a document. */
  draftSnapshotSceneDiffDocumentPrefix: (projectId: string | number, documentId: string | number) =>
    [
      "script",
      "draft-snapshot-scene-diff",
      normalizeId(projectId),
      normalizeId(documentId),
    ] as const,
  scriptRevisions: (projectId: string | number, documentId: string | number) =>
    ["script", "revisions", normalizeId(projectId), normalizeId(documentId)] as const,
  scriptRevisionCurrent: (projectId: string | number, documentId: string | number) =>
    ["script", "revisions", "current", normalizeId(projectId), normalizeId(documentId)] as const,
  /** Prefix for every locked snapshot's revision-mark status of a document. */
  scriptRevisionMarksDocumentPrefix: (projectId: string | number, documentId: string | number) =>
    ["script", "revision-marks", normalizeId(projectId), normalizeId(documentId)] as const,
  scriptRevisionMarks: (
    projectId: string | number,
    documentId: string | number,
    lockedSnapshotId: string
  ) =>
    [
      "script",
      "revision-marks",
      normalizeId(projectId),
      normalizeId(documentId),
      lockedSnapshotId,
    ] as const,
  scriptShareLinks: (projectId: string | number, documentId: string | number) =>
    ["script", "share-links", normalizeId(projectId), normalizeId(documentId)] as const,
  sharedScript: (token: string, turnstileKey = "", sharePasswordAttempt = 0, authSessionKey = "") =>
    ["script", "shared", token, turnstileKey, sharePasswordAttempt, authSessionKey] as const,
  commentsSummary: (projectId: string | number, documentId: string | number) =>
    ["script", "comments-summary", normalizeId(projectId), normalizeId(documentId)] as const,
  comments: (
    projectId: string | number,
    documentId: string | number,
    filters?: { sceneId?: number; status?: "open" | "resolved" }
  ) =>
    [
      "script",
      "comments",
      normalizeId(projectId),
      normalizeId(documentId),
      filters?.sceneId ?? null,
      filters?.status ?? null,
    ] as const,
  /**
   * Prefix shared by every `comments()` query for this document (any scene/status filters).
   * Use with `invalidateQueries` so scene-scoped lists refetch after comment or draft updates.
   */
  commentsDocumentScope: (projectId: string | number, documentId: string | number) =>
    ["script", "comments", normalizeId(projectId), normalizeId(documentId)] as const,
  commentCount: (projectId: string | number, documentId: string | number) =>
    ["script", "comment-count", normalizeId(projectId), normalizeId(documentId)] as const,
  commentThreadReads: (projectId: string | number, documentId: string | number) =>
    ["script", "comment-thread-reads", normalizeId(projectId), normalizeId(documentId)] as const,
  notes: (projectId: string | number, documentId: string | number, sceneId: string | number) =>
    ["notes", normalizeId(projectId), normalizeId(documentId), normalizeId(sceneId)] as const,
  // Nested under `script` so `invalidateQueries({ queryKey: scriptKeys.all })` after
  // script breakdown also refreshes tags-report (budget/schedule education banners).
  tags: (projectId: string | number, documentId: string | number) =>
    ["script", "tags", normalizeId(projectId), normalizeId(documentId)] as const,
  tagCategories: (projectId: string | number, documentId: string | number) =>
    ["script", "tagCategories", normalizeId(projectId), normalizeId(documentId)] as const,
};
